/**
 * @fileOverview Renders a table of data with search, filters and column sort.
 * The api is simple but has reduced functionality. Only works if you know all
 * the data upfront. Things you can't do with this component are.
 *   • Asynchronous data fetching, use DateFetchingTableTable
 *   • Low level, granular, UI control, use TableTable
 *   • No flexibility with custom headers or header naming
 *
 * This component is a layer over the top of the DataFetchingTableTable (as
 * that handles all filter and search state) which in itself is a layer over the
 * top of the TableTable which handles all UI.
 */
import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import TableTable from '../TableTable'
import { filterTableDataLogic, sortTableDataLogic } from './util'
import { SORT_DIRECTION } from '../TableTable/types'

const DEFAULT_MAX_RECORDS = 1000

/**
 * @typedef LabelSort
 * @property {string} label
 * @property {Function} sort A sort function, will pass in the labels as
 * arguments one and two.
 *
 * @typedef SimpleTableTableProps
 * @property {Array<object>} data Each key represents the matching filter or
 * column headers. So it's better to make the key something readable. And the
 * value is the raw value without any formatting. This is so we can get things
 * like sorting and filtering working without any extra effort. If you need
 * to do any custom formatting, use the method `postRowRenderer`.
 * @property {Array<object>} footer Footer of the table for displaying info like "Total:",
 * has same data structure as `data`
 * @property {Array<string|LabelSort>} filterOn Provides filters to filter on these keys
 * @property {Array<string|LabelSort>} sortOn Enables sorting on these column headers
 * @property {Array<string>} searchOn Enables search to work on data for these
 * keys.
 * @property {Array<string>} header An array of headers to display. The values
 * are what are used for the headers but also what's used as the key for the
 * data, so ensure these match.
 * @property {Function} postRowRender Called before a row is rendered, it's
 * first argument will be the data object, replace any values with how you
 * want the cell to be rendered and return the new data object.
 * @property {Function} action Fired when a row is clicked, the first and only
 * parameter will be the row data.
 *
 * @param {SimpleTableTableProps} props
 * Example usage
 * ```
 *  <SimpleTableTable
 *    header={['City', 'Week', 'Location', 'Status']}
 *    data={[
 *      {
 *        City: 'Paris',
 *        Week: '28',
 *        Location: 'Central Station',
 *        Status: 'COMPLETE',
 *      },
 *    ]}
 *    filterOn={['City', { label: 'Location', sort: customSortFunc }]}
 *    sortOn={['Status']}
 *    searchOn={['Location']}
 *    postRowRender={m => ({
 *      ...m,
 *      // Render a custom UI for the status
 *      'Status': <StatusLabel status={m.Status}>{m.Status}</StatusLabel>,
 *    })}
 *  />
 * ```
 */
const SimpleTableTable = props => {
  const {
    data,
    filterOn = [],
    sortOn = [],
    searchOn = [],
    postRowRender = t => t,
    header,
    footerRender,
    action = () => {},
    showPages,
    loadingData = false,
    onTableDataUpdate = () => {},
    itemsPerPage,
  } = props
  const [sortBy, setSortBy] = useState(null) // {column: HEADER_LABEL, direction: SORT_DIRECTION.DECENDING}
  const [selectedFilters, setSelectedFilters] = useState({})
  const [searchText, setSearchText] = useState('')
  const [offsetRecordsStart, setOffsetRecordsStart] = useState(0)

  const resultsPerPage =
    itemsPerPage || showPages === false ? DEFAULT_MAX_RECORDS : 50

  const showSearch = !!searchOn.length

  const onOffsetChange = offset => {
    if (offset < 0) offset = 0
    if (offset > totalRecords) return
    if (props.onOffsetChange) return props.onOffsetChange(offset)
    setOffsetRecordsStart(offset)
  }

  const bindDataReset = func => (...args) => {
    func(...args)
    // Reset the paging
    setOffsetRecordsStart(0)
  }

  // Filter out the sort configuration if it is specified
  var sortableHeaders = sortOn.map(label =>
    typeof label === 'object' ? label.label : label,
  )

  const tableTableFilters = filterOn.map(filterRecord => {
    const label =
      typeof filterRecord === 'object' ? filterRecord.label : filterRecord
    const sortFn =
      typeof filterRecord === 'object' ? filterRecord.sort : undefined

    // Figure out all possible values
    const uniqueValues = filterRecord.values
      ? filterRecord.values
      : Array.from(new Set(data.map(dto => dto[label])))
    uniqueValues.sort(sortFn)
    return {
      ...filterRecord,
      label,
      values: uniqueValues,
      selectedValue: selectedFilters[label],
      onChange: bindDataReset(newVal => {
        var newSelectedFilters = {
          ...selectedFilters,
        }
        newSelectedFilters[label] = newVal == null ? null : newVal
        setSelectedFilters(newSelectedFilters)

        onTableDataUpdate({
          displayedData: filterTableDataLogic({
            data,
            searchOn,
            selectedFilters: newSelectedFilters,
            searchText,
          }),
          selectedFilters: newSelectedFilters,
          sortBy,
          searchText,
        })
      }),
    }
  })

  const tableTableHeader = header.map(label => {
    const sortEnabled = !sortableHeaders || sortableHeaders.includes(label)

    return {
      label,
      sortDirection:
        sortEnabled && sortBy && sortBy.column == label
          ? sortBy.direction
          : SORT_DIRECTION.NO_SORT,
      onSortDirectionChange: sortEnabled
        ? bindDataReset(() => {
            const nextSortBy = {
              column: label,
              direction:
                sortBy &&
                sortBy.column == label &&
                sortBy.direction === SORT_DIRECTION.DECENDING
                  ? SORT_DIRECTION.ASCENDING
                  : SORT_DIRECTION.DECENDING,
            }
            setSortBy(nextSortBy)

            onTableDataUpdate({
              displayedData: filteredData,
              selectedFilters,
              sortBy: nextSortBy,
              searchText,
            })
          })
        : null,
    }
  })

  // Data operations:
  // Filter data
  const filteredData = useMemo(
    () =>
      filterTableDataLogic({
        data,
        searchOn,
        selectedFilters,
        searchText,
      }),
    [data, searchOn, selectedFilters, searchText],
  )
  // Sort data
  const orderedData = useMemo(
    () =>
      sortTableDataLogic({
        data: filteredData,
        sortOn,
        sortBy,
      }),
    [filteredData, sortOn, sortBy],
  )

  // Do pagination
  const totalRecords = props.totalRecords || orderedData.length
  const paginatedData = useMemo(
    () =>
      orderedData.slice(
        offsetRecordsStart,
        resultsPerPage + offsetRecordsStart,
      ),
    [orderedData, offsetRecordsStart, resultsPerPage],
  )

  // Do custom rendering
  const formattedData = useMemo(() => paginatedData.map(postRowRender), [
    paginatedData,
    postRowRender,
  ])

  // Put into a flat array for rendering
  const tableRows = formattedData.map(dto =>
    header.map(
      (key, index, array) => dto[key] || dto[array[index - 1] + ' ' + key],
    ),
  )
  const rowsActions = formattedData.map(dto => () => action(dto))

  // Footer
  const footerRows =
    typeof footerRender === 'function'
      ? footerRender(filteredData).map(dto =>
          header.map(
            (key, index, array) =>
              dto[key] || dto[array[index - 1] + ' ' + key],
          ),
        )
      : null

  const onSearchTextChange = nextSearchText => {
    setSearchText(nextSearchText)
    onTableDataUpdate({
      displayedData: filteredData,
      selectedFilters,
      sortBy,
      searchText: nextSearchText,
    })
  }

  return (
    <TableTable
      data={tableRows}
      filters={tableTableFilters}
      header={tableTableHeader}
      actions={rowsActions}
      footer={footerRows}
      sortableHeaders={sortableHeaders}
      totalRecords={totalRecords}
      offsetRecordsStart={offsetRecordsStart}
      onOffsetChange={onOffsetChange}
      resultsPerPage={resultsPerPage}
      showPages={showPages}
      searchText={showSearch ? searchText : undefined}
      onSearchTextChange={showSearch ? onSearchTextChange : undefined}
      loadingData={loadingData}
    />
  )
}

SimpleTableTable.propTypes = {
  //Array of object for data rendering. Keys in the object must match provided headers
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  filterOn: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        sort: PropTypes.func,
      }),
    ]),
  ),
  // Columns of the table
  header: PropTypes.arrayOf(PropTypes.string).isRequired,
  // Sortable columns
  sortOn: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        label: PropTypes.string.isRequired,
      }),
    ]),
  ),
  // Searchable columns
  searchOn: PropTypes.arrayOf(PropTypes.string),
  postRowRender: PropTypes.func,
  footerRender: PropTypes.func,
  action: PropTypes.func,
  showPages: PropTypes.bool,
  loadingData: PropTypes.bool,
}

export default React.memo(SimpleTableTable)



// WEBPACK FOOTER //
// ./src/Components/SimpleTableTable/index.js